import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useHealthCheck } from '../hooks/useHealthCheck';
import { logger } from '../lib/utils/logger';
import { authService } from '../lib/services/auth';
import { ThemedCard } from '../components/themed';
import { SSOProviders } from '../components/auth/SSOProviders';
import { Mail, Lock, AlertCircle, Loader2, Shield, X } from 'lucide-react';
import { useFormSubmission } from '../hooks/useFormSubmission';
import { hasStatusCode, isErrorType } from '../lib/utils/errorHandling';
import { SkipLink } from '../components/SkipLink';
import { STORAGE_KEYS } from '../lib/config/constants';
import { APP_ROUTES } from '../config/routes';
import ThemeToggle from '../components/ThemeToggle';

/**
 * Friendly messages for the `sso_error` codes the server-side SSO callback
 * appends when it redirects back to `/login?sso_error=<code>`.
 */
const SSO_ERROR_MESSAGES: Record<string, string> = {
  denied: 'Sign-in was cancelled.',
  invalid_request: 'Single sign-on failed. Please try again.',
  expired: 'Your sign-in session expired. Please try again.',
  config_missing: 'Single sign-on is not fully configured. Please contact your administrator.',
  auth_failed: 'We could not sign you in with your identity provider.',
  email_conflict: 'An account with that email needs attention. Please contact your administrator.',
};

function ssoErrorMessage(code: string): string {
  return SSO_ERROR_MESSAGES[code] ?? 'Single sign-on failed. Please try again.';
}

/**
 * Login page component that handles user authentication.
 */
const Login: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [rememberMe, setRememberMe] = useState(
    () => localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true',
  );
  const [email, setEmail] = useState(() => {
    // One-time pre-fill from URL (e.g., after email verification).
    const fromQuery = searchParams.get('email');
    if (fromQuery !== null && fromQuery !== '') {
      return fromQuery;
    }
    if (rememberMe) {
      return localStorage.getItem(STORAGE_KEYS.LAST_LOGIN_EMAIL) ?? '';
    }
    return '';
  });
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Banner shown when the server-side SSO callback bounced back with an error.
  const [ssoError, setSsoError] = useState<string | null>(() => {
    const code = searchParams.get('sso_error');
    return code !== null && code !== '' ? ssoErrorMessage(code) : null;
  });
  // Steering prompt that opens the SSO control when a password login attempt
  // is rejected because the email's domain is SSO-enforced.
  const [ssoPrompt, setSsoPrompt] = useState<string | null>(null);
  const navigate = useNavigate();
  const healthStatus = useHealthCheck();

  const { isSubmitting, handleSubmit, getFieldProps } = useFormSubmission({
    onSubmit: async () => {
      setError(null);

      const response = await authService.login({
        email,
        password,
        rememberMe,
        otp: otp || undefined,
      });

      if (rememberMe) {
        localStorage.setItem(STORAGE_KEYS.LAST_LOGIN_EMAIL, email);
      } else {
        localStorage.removeItem(STORAGE_KEYS.LAST_LOGIN_EMAIL);
      }

      // An invitation acceptance is in flight (arrived from
      // /accept-invitation → /login?invite_token=…). Bounce straight back to
      // the accept page so the now-authenticated user lands in the one-click
      // Accept branch, rather than being dropped on the org list.
      const inviteToken = searchParams.get('invite_token');
      if (inviteToken !== null && inviteToken !== '') {
        navigate(`${APP_ROUTES.ACCEPT_INVITATION}?token=${encodeURIComponent(inviteToken)}`, {
          replace: true,
        });
        return;
      }

      // Multi-org user needs to pick an organization first
      if (response.requiresOrgSelection) {
        logger.info('Login successful, multi-org user — routing to my-organizations');
        navigate(APP_ROUTES.ORGANIZATIONS, { replace: true });
        return;
      }

      // Single-org or zero-org user — PostLoginRouter handles the rest
      logger.info('Login successful, redirecting to welcome page');
      navigate(APP_ROUTES.WELCOME, { replace: true });
    },
    onError: (error) => {
      logger.error('Login error:', error);
      // Detect typed `400 mfa_required` from the backend's MFA-aware
      // login pipeline and surface an OTP-specific prompt instead of
      // the generic invalid-credentials message.
      const detail = (error as { details?: { detail?: { type?: string } } }).details?.detail;
      if (hasStatusCode(error, 400) && detail?.type === 'mfa_required') {
        setError('Enter the code from your authenticator app to continue.');
        return;
      }
      // The email's domain enforces SSO — steer the user to the SSO control
      // instead of showing a generic credential error.
      if (hasStatusCode(error, 400) && detail?.type === 'sso_required') {
        setSsoPrompt('Your organization uses single sign-on. Sign in with SSO.');
        return;
      }
      if (hasStatusCode(error, 403) || isErrorType(error, 'AuthorizationError')) {
        setError('Unable to log in. Please check your credentials and try again.');
      } else if (hasStatusCode(error, 401) || isErrorType(error, 'AuthenticationError')) {
        setError('Invalid email or password. Please try again.');
      } else {
        setError('Unable to log in. Please try again later.');
      }
    },
  });

  return (
    <>
      <SkipLink />
      <main className="min-h-screen flex items-center justify-center surface-secondary relative">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        <div className="w-96 flex flex-col items-center gap-6" id="main-content">
          {/* Branding */}
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-2">
              <img src="/coldsea-logo.svg" alt="" className="h-7 dark:invert" aria-hidden="true" />
              <h1 className="text-3xl font-bold text-primary">Deploy</h1>
            </div>
          </div>

          <ThemedCard className="w-full">
            <form onSubmit={handleSubmit} className="flex flex-col" data-testid="login-form">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-center mb-2 text-primary">Login</h2>
                <p className="text-sm text-center text-secondary mb-6">
                  Welcome back! Please login to your account.
                </p>

                {!healthStatus.isHealthy && (
                  <div className="mb-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg flex gap-2">
                    <AlertCircle className="h-5 w-5 text-orange-700 dark:text-orange-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-base font-medium text-orange-800 dark:text-orange-200">
                        System Unavailable
                      </p>
                      <p className="text-sm mt-1 text-orange-700 dark:text-orange-300">
                        The system is currently unavailable. Please try again later.
                      </p>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                  </div>
                )}

                {ssoError && (
                  <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start justify-between gap-2">
                    <p className="text-sm text-red-600 dark:text-red-400">{ssoError}</p>
                    <button
                      type="button"
                      onClick={() => setSsoError(null)}
                      className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 shrink-0"
                      aria-label="Dismiss"
                      data-testid="login-sso-error-dismiss"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}

                <SSOProviders mode="signin" prompt={ssoPrompt} initialEmail={email} />

                <div className="space-y-4">
                  <div>
                    <label htmlFor="email" className="form-label">
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-zinc-400 dark:text-zinc-500" />
                      </div>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="form-input pl-11!"
                        placeholder="you@example.com"
                        data-testid="login-email-input"
                        {...getFieldProps()}
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="password" className="form-label">
                      Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-zinc-400 dark:text-zinc-500" />
                      </div>
                      <input
                        id="password"
                        type="password"
                        name="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="form-input pl-11!"
                        placeholder="Enter your password"
                        data-testid="login-password-input"
                        {...getFieldProps()}
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="otp" className="form-label">
                      {recoveryMode ? 'Recovery code' : 'Authentication code'}
                      <span className="ml-1 text-xs text-secondary">(if enrolled)</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Shield className="h-5 w-5 text-zinc-400 dark:text-zinc-500" />
                      </div>
                      <input
                        id="otp"
                        type="text"
                        name="otp"
                        autoComplete="one-time-code"
                        inputMode={recoveryMode ? 'text' : 'numeric'}
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        maxLength={recoveryMode ? 10 : 6}
                        className="form-input pl-11!"
                        placeholder={recoveryMode ? '10-character recovery code' : '6-digit code'}
                        data-testid="login-otp-input"
                        {...getFieldProps()}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setRecoveryMode(!recoveryMode);
                        setOtp('');
                      }}
                      className="text-xs text-sky-600 dark:text-sky-400 hover:underline mt-1"
                      data-testid="login-toggle-recovery"
                    >
                      {recoveryMode ? 'Use authenticator code' : 'Use a recovery code'}
                    </button>
                  </div>
                </div>

                <div className="flex items-center mt-4">
                  <input
                    id="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => {
                      setRememberMe(e.target.checked);
                      localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, String(e.target.checked));
                    }}
                    className="h-4 w-4 text-sky-600 rounded-sm border-zinc-300 dark:border-zinc-600 focus:ring-sky-500"
                    data-testid="login-remember-me"
                  />
                  <label htmlFor="remember-me" className="ml-2 text-sm text-secondary">
                    Remember me
                  </label>
                </div>

                <div className="mt-6">
                  <button
                    type="submit"
                    disabled={!healthStatus.isHealthy || isSubmitting}
                    className="w-full px-4 py-2.5 bg-sky-600 hover:bg-sky-700 disabled:bg-zinc-400 dark:disabled:bg-zinc-600 text-white font-medium rounded-lg transition-colors disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="login-submit-button"
                  >
                    {(() => {
                      if (isSubmitting) {
                        return (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Signing in...</span>
                          </>
                        );
                      }
                      if (!healthStatus.isHealthy) {
                        return 'System Unavailable';
                      }
                      return 'Sign In';
                    })()}
                  </button>
                </div>
              </div>
              <div className="px-6 pb-6 pt-0">
                <div className="flex justify-between items-center">
                  <Link
                    to={APP_ROUTES.RESET_PASSWORD_REQUEST}
                    className="text-sm text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300"
                    data-testid="login-forgot-password-link"
                  >
                    Forgot Password?
                  </Link>
                  <Link
                    to={APP_ROUTES.SIGNUP}
                    className="text-sm text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300"
                    data-testid="login-signup-link"
                  >
                    Create Account
                  </Link>
                </div>
              </div>
            </form>
          </ThemedCard>
        </div>
      </main>
    </>
  );
};

export default Login;
